import { useState, useCallback } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  GreenContainer,
  GreenContainerQuestionWrapper,
  ClaimsContainer
} from 'src/components/application/defaultComponents'
import PrebuiltRenewalFooter from 'src/components/application/PrebuiltRenewalFooter'
import { useRenewal } from 'src/context/renewal'
import { LeftBar } from 'src/components/basic/LeftBar'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import StepBullet from 'src/components/basic/StepBullet'
import OptionButton from 'src/components/basic/OptionButton'
import ClaimItemForm from 'src/components/application/formComponents/ClaimItemForm'
import flagWithCheckImg from 'src/assets/applicationFormsImgs/flag_with_check.svg'
import { ClaimData } from 'src/dtos/applicationData'
import { ClaimDataWithId } from 'src/dtos/custom'
import Button from 'src/components/basic/Button'
import { generateRandomNumberId } from 'src/utils/math'
import { isJudgeClaimsTriggered } from 'src/utils/renewals'
import { RenewalData } from 'src/dtos/renewalData'
import { string } from 'yup'

interface MalpracticeData {
  isVisible: boolean;
  value: boolean | undefined;
}

const Claims = () => {
  const judgeRenewalClaimErrorCodes = ['EC148', 'PH', 'PH', 'EC149', 'EC150', 'EC151', 'EC156', 'EC157', 'EC158', 'EC159', 'EC160',
                                       'EC161', 'EC162', 'EC163', 'EC164', 'EC165', 'EC166', 'EC167', 'EC168', 'EC169', 'EC170', 'EC152',
                                       'EC153', 'EC154', 'EC155']
  const { renewalData } = useRenewal()
  const [claims, setClaims] = useState<ClaimDataWithId[]>(() => {
    return renewalData?.judgeRenewalSection?.questions?.claims?.map(claim => ({ ...claim, id: generateRandomNumberId() })) || 
      [{ id: generateRandomNumberId() }]
  })
  const [isIncorrect, setIsIncorrect] = useState(false)
  const [hasBeenSubjectOfClaim, setHasBeenSubjectOfClaim] = useState<MalpracticeData>(() => {
    if(renewalData?.judgeRenewalSection?.questions?.hasBeenSubjectOfClaim) {
      const { hasBeenSubjectOfClaim } = renewalData?.judgeRenewalSection?.questions

      return {
        isVisible: !!hasBeenSubjectOfClaim,
        value: hasBeenSubjectOfClaim
      }
    }
    return { isVisible: false, value: false }
  })
  const [hasClaimBeenReported, setHasClaimBeenReported] = useState<MalpracticeData>(() => {
    if(renewalData?.judgeRenewalSection?.questions?.hasClaimBeenReported !== undefined) {
      const { hasClaimBeenReported } = renewalData?.judgeRenewalSection?.questions

      return {
        isVisible: hasClaimBeenReported !== undefined,
        value: hasClaimBeenReported
      }
    }
    return { isVisible: false, value: false }
  })
  const [hasBasisForClaim, setHasBasisForClaim] = useState<MalpracticeData>(() => {
    if(renewalData?.judgeRenewalSection?.questions?.hasBasisForClaim) {
      const { hasBasisForClaim } = renewalData?.judgeRenewalSection?.questions

      return {
        isVisible: !!hasBasisForClaim,
        value: hasBasisForClaim
      }
    }
    return { isVisible: false, value: false }
  })
  const [hasBasisForClaimReported, setHasBasisForClaimReported] = useState<MalpracticeData>(() => {
    if(renewalData?.judgeRenewalSection?.questions?.hasBasisForClaimReported !== undefined) {
      const { hasBasisForClaimReported } = renewalData?.judgeRenewalSection?.questions

      return {
        isVisible: hasBasisForClaimReported !== undefined,
        value: hasBasisForClaimReported
      }
    }
    return { isVisible: false, value: false }
  })

  const isAllSet = () => {
    return !isJudgeClaimsTriggered({
      ...renewalData,
      judgeRenewalSection: {
        ...renewalData?.judgeRenewalSection,
        questions: {
          ...renewalData?.judgeRenewalSection?.questions,
          hasBeenSubjectOfClaim: hasBeenSubjectOfClaim.value,
          hasClaimBeenReported: hasClaimBeenReported.value,
          hasBasisForClaim: hasBasisForClaim.value,
          hasBasisForClaimReported: hasBasisForClaimReported.value
        }
      }
    })
  }

  const updateClaims = useCallback((index: number, data: Partial<ClaimData>) => {
    setClaims(prev => prev.map((pv, idx) => idx === index ? { ...pv, ...data } : pv))
  }, [])

  const handleAddAnotherClaim = () => {
    setClaims(prev => [...prev, { id: generateRandomNumberId() }])
  }

  const updatedApplicationData = (): RenewalData => ({
    ...renewalData,
    currentPage: window.location.pathname,
    judgeRenewalSection: {
      ...renewalData?.judgeRenewalSection,
      questions: {
        ...renewalData?.judgeRenewalSection?.questions,
        hasBeenSubjectOfClaim: hasBeenSubjectOfClaim.value,
        hasClaimBeenReported: hasClaimBeenReported.value,
        hasBasisForClaim: hasBasisForClaim.value,
        hasBasisForClaimReported: hasBasisForClaimReported.value,
        claims: claims.map(claim => {
          const { id, ...rest } = claim
          return rest
        })
      }
    }
  })

  const validatePage = () => {
    const validation = claims.map(claim => {
      try {
        if(!!claim?.claimDate) {
          string().trim().length(10).validDate('Invalid Date').validateSync(claim?.claimDate)
        }
        if(!!claim?.allegedErrorDate) {
          string().trim().length(10).validDate('Invalid Date').validateSync(claim?.allegedErrorDate)
        }
        return true
      } catch(e) {
        return false
      }
    })

    return !validation.some(valid => valid === false)
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding your claim.</DefaultTitle>
        <GreenContainer>
          <div className='green-container-content'>
            <h6>You’re being asked to fill out this supplemental claim information based on your answers to the following questions.</h6>
            <span>If this information is incorrect, please change your answers.</span>
            <div>
              <LeftBar />
              <GreenContainerQuestionWrapper>
              {hasBeenSubjectOfClaim.isVisible ? (
                  <div className='green-container-question-div'>
                    <StepBullet>9</StepBullet>
                    <FlexDiv dir='column' gap="24px">
                      <p>
                      In the last 12 months, has the applicant been the subject of a professional or judicial liability claim?
                      </p>
                      <FlexDiv gap="16px">
                        <OptionButton
                          checked={hasBeenSubjectOfClaim.value === true}
                          onChange={() => {
                            setHasBeenSubjectOfClaim(prev => ({ ...prev, value: true }))
                            setHasClaimBeenReported({ isVisible: true, value: renewalData?.renewalSection?.hasMalPracticeClaimsReported })
                          }}
                        >
                          Yes
                        </OptionButton>
                        <OptionButton
                          checked={hasBeenSubjectOfClaim.value === false}
                          onChange={() => {
                            setHasBeenSubjectOfClaim(prev => ({ ...prev, value: false }))
                            setHasClaimBeenReported({ isVisible: false, value: undefined })
                          }}
                        >
                          No
                        </OptionButton>
                      </FlexDiv>
                      {hasClaimBeenReported.isVisible ? (
                        <FlexDiv gap="24px">
                          <LeftBar />
                          <FlexDiv dir="column">
                            <span style={{margin: 0}}>Has the claim been previously reported to TLIE?</span>
                            <FlexDiv gap="16px" margin="8px 0 0 0">
                              <OptionButton
                                checked={hasClaimBeenReported.value === true}
                                onChange={() => setHasClaimBeenReported(prev => ({ ...prev, value: true }))}
                              >
                                Yes
                              </OptionButton>
                              <OptionButton
                                checked={hasClaimBeenReported.value === false}
                                onChange={() => setHasClaimBeenReported(prev => ({ ...prev, value: false }))}
                              >
                                No
                              </OptionButton>
                            </FlexDiv>
                          </FlexDiv>
                        </FlexDiv>
                      ) : null}
                    </FlexDiv>
                  </div>
                ) : null}
                {hasBasisForClaim.isVisible ? (
                  <div className='green-container-question-div'>
                    <StepBullet>10</StepBullet>
                    <FlexDiv dir='column' gap="24px">
                      <p>
                      Does the applicant know, have reason to know, or have any basis to believe that a circumstance, act, error or omission might reasonably be expected to be the basis for a professional or judicial liability claim against the applicant?
                      </p>
                      <FlexDiv gap="16px">
                        <OptionButton
                          checked={hasBasisForClaim.value === true}
                          onChange={() => {
                            setHasBasisForClaim(prev => ({ ...prev, value: true }))
                            setHasBasisForClaimReported(({ isVisible: true, value: renewalData?.renewalSection?.hasMalPracticeBasisReported }))
                          }}
                        >
                          Yes
                        </OptionButton>
                        <OptionButton
                          checked={hasBasisForClaim.value === false}
                          onChange={() => {
                            setHasBasisForClaim(prev => ({ ...prev, value: false }))
                            setHasBasisForClaimReported(({ isVisible: false, value: undefined }))
                          }}
                        >
                          No
                        </OptionButton>
                      </FlexDiv>
                      {hasBasisForClaimReported.isVisible ? (
                        <FlexDiv gap="24px">
                          <LeftBar />
                          <FlexDiv dir="column">
                            <span style={{margin: 0}}>Has the claim been previously reported to TLIE?</span>
                            <FlexDiv gap="16px" margin="8px 0 0 0">
                              <OptionButton
                                checked={hasBasisForClaimReported.value === true}
                                onChange={() => setHasBasisForClaimReported(prev => ({ ...prev, value: true }))}
                              >
                                Yes
                              </OptionButton>
                              <OptionButton
                                checked={hasBasisForClaimReported.value === false}
                                onChange={() => setHasBasisForClaimReported(prev => ({ ...prev, value: false }))}
                              >
                                No
                              </OptionButton>
                            </FlexDiv>
                          </FlexDiv>
                        </FlexDiv>
                      ) : null}
                    </FlexDiv>
                  </div>
                ) : null}
              </GreenContainerQuestionWrapper>
            </div>
          </div>
        </GreenContainer>
        {isAllSet() ? (
          <GreenContainer>
            <div className='claim-all-set'>
              <div className='claim-all-set-icon'>
                <img src={flagWithCheckImg} alt="All Set" />
              </div>
              <div className='claim-all-set-message'>
                <h3>All Set!</h3>
                <h4>You no longer need to complete this section.</h4>
              </div>
            </div>
          </GreenContainer>
        ) : (
          <ClaimsContainer>
            <span className='claims-form-header-subtitle'>PLEASE FILL OUT THE FOLLOWING TO THE BEST OF YOUR ABILITY</span>
            <span className='claims-form-header-subtitle'>If you do not know, leave blank.</span>
            {claims.map((claim, index) => (
              <ClaimItemForm
                key={claim.id}
                claim={claim}
                claimIndex={index}
                attorneys={undefined}
                setClaim={(data) => updateClaims(index, data)}
                isJudge
                isIncorrect={isIncorrect && !validatePage()}
                errorCodes={judgeRenewalClaimErrorCodes.map(e => `${e}:${index}`)}
              />
            ))}
            <div className='claims-add-button'>
              <Button variant='secondary' onClick={handleAddAnotherClaim}>
                Add another claim
              </Button>
            </div>
          </ClaimsContainer>
        )}
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/judge/questions/main"
        nextRoute='/customer/renew/judge/quote/details'
        updatedApplication={updatedApplicationData()}
        isFormValid={validatePage()}
        beforeSaveContinueClick={() => !validatePage() ? setIsIncorrect(true) : null}
      />
    </ApplicationBodyContainer>
  )
}

export default Claims